import React, { useEffect } from "react";
import dayjs from "dayjs";
import router from "next/router";

import {
  BookingCardStatusContainer,
  BadgeContainer,
  TicketHolder,
  TicketHolderContainer,
  BookingCardContainer,
  BookingCardContentContainer,
  UserStatusContainer,
  ButtonsContainer,
} from "./BookingCard.styles";
import { SessionCardBottomBar } from "../SessionCard/SessionCard.styles";
import {
  MeshGradient,
  MeshColours,
  SessionDetails,
  TicketIcon,
  Badge,
  generateSessionDetails,
  Countdown,
  TicketDetails,
  StatusTag,
  StatusTagVariants,
  Button,
  ManageBookingMenu,
  TicketVariantsType,
} from "..";
import { SessionDetailsInterface } from "../../models/Session";
import { UserBookingInterest } from "../../models/BookingInterest";
import { Icon } from "../../icons";
import { Box } from "@mui/material";
import { useModal } from "src/contexts/ModalContext";
import { IBookingTicket, IUserBookingTicket } from "src/models/BookingTicket";
import { AllocationType } from "src/models/AllocationV2";

export const getTicketType = (ticketHolders: TicketDetails[], allocationType?: AllocationType | undefined,) => {
  let ticketType: TicketVariantsType = "generalAdmission";

  const hasPriority = ticketHolders?.some((element) => element.isPriorityTicket === true);
  const hasAccessibility = ticketHolders?.some((element) => element.hasAccessibilityRequirements === true);

  switch (allocationType) {
    case "FriendsAndFamily":
      ticketType = "friendsAndFamily"
      break;
    case "VIP":
      ticketType = "vip"
      break;
    case "Priority":
      ticketType = hasAccessibility ? "priorityAccessibility" : "priority";
      break;
    default:
      if (hasPriority && hasAccessibility) {
        ticketType = "priorityAccessibility";
      } else if (hasPriority) {
        ticketType = "priority";
      } else if (hasAccessibility) {
        ticketType = "accessibilityRequired";
      }
  }

  return ticketType;
}

export interface CustomBookingCardProps {
  bookingInterest: UserBookingInterest;
  colour?: MeshColours;
  isTicketGenerating?: boolean;
  handleAcceptTicket: (bookingInterest: UserBookingInterest) => Promise<void>;
  handleDeclineTicket: (bookingInterest: UserBookingInterest) => Promise<void>;
  handleDeclineAttendance: (bookingInterest: UserBookingInterest) => Promise<void>;
  handleDownloadTicket: (userEmail: string, ticketId: number) => Promise<void>;
  onModalAction?: (successful?: boolean, message?: React.ReactElement | string) => void;
}

export const BookingCard = ({
  bookingInterest,
  isTicketGenerating = false,
  handleAcceptTicket,
  handleDeclineTicket,
  handleDeclineAttendance,
  handleDownloadTicket,
  onModalAction,
  colour = "pink-purple-green",
  ...rest
}: CustomBookingCardProps) => {
  const { session, user, ticket, status } = bookingInterest;
  const { modalBookingData, setCurrentBookingData } = useModal();

  const allocationSession = bookingInterest.allocationSession;

  const [sessionDetails, setSessionDetails] =
    React.useState<SessionDetailsInterface>({
      name: session.name,
      date: "",
      arrivalTime: "",
      approxFinishTime: "",
      location: null,
    });
  const [isAcceptLoading, setAcceptLoading] = React.useState<boolean>(false)
  const [isDeclineLoading, setDeclineLoading] = React.useState<boolean>(false)
  const [isDeclineAttendanceLoading, setDeclineAttendanceLoading] = React.useState<boolean>(false)
  const [isDownloadLoading, setDownloadLoading] = React.useState<boolean>(false)
  const [isConfirmAttendanceLoading, setConfirmAttendanceLoading] = React.useState<boolean>(false)

  const [minAge, setMinAge] = React.useState<number | undefined>(0)

  const handleDownloadTicketInternal = async (userEmail: string, ticketId: number) => {
    setDownloadLoading(true);
    await handleDownloadTicket(userEmail, ticketId)
    setDownloadLoading(false);
  }

  const handleAcceptTicketInternal = async (bookingInterest: UserBookingInterest) => {
    setAcceptLoading(true);
    await handleAcceptTicket(bookingInterest)

    setAcceptLoading(false);
  }

  const handleDeclineTicketInternal = async (bookingInterest: UserBookingInterest) => {
    setDeclineLoading(true);
    await handleDeclineTicket(bookingInterest)
    setDeclineLoading(false);
  }

  const handleDeclineAttendanceInternal = async (bookingInterest: UserBookingInterest) => {
    setDeclineAttendanceLoading(true);
    await handleDeclineAttendance(bookingInterest)
    setDeclineAttendanceLoading(false);
  }

  const handleConfirmAttendanceInternal = async (bookingInterest: UserBookingInterest) => {
    setConfirmAttendanceLoading(true);
    router.push({
      pathname: "/attendance",
      query: { bookingInterestId: bookingInterest.id },
    })
    setConfirmAttendanceLoading(false);
  }

  const fillBookingData = (bookingInterestToFillWith: UserBookingInterest) => {
    return {
      id: bookingInterestToFillWith.id,
      loggedInUserTicket: {
        id: bookingInterestToFillWith.user.id,
        firstName: bookingInterestToFillWith.assignedFirstName ?? bookingInterestToFillWith.user.firstName,
        lastName: bookingInterestToFillWith.assignedLastName ?? bookingInterestToFillWith.user.lastName,
        email: bookingInterestToFillWith.user.email,
        age: bookingInterestToFillWith.userAgeAtBooking ?? bookingInterestToFillWith.user.age,
        accessibility: bookingInterestToFillWith.accessibility ?? bookingInterestToFillWith.user.accessibility ?? "None",
        otherAccessibility: bookingInterestToFillWith.otherAccessibility ?? bookingInterestToFillWith.user.otherAccessibility ?? "",
        status: bookingInterestToFillWith.status,
        parentId: bookingInterestToFillWith.parentId
      } as IBookingTicket,
      ticketForAnotherPerson: bookingInterestToFillWith.relatedBookingInterests?.map(item => {
        return {
          bookingInterestId: item.id,
          id: item.user.id,
          firstName: item.assignedFirstName ?? item.user.firstName,
          lastName: item.assignedLastName ?? item.user.lastName,
          email: item.user.email,
          age: item.userAgeAtBooking ?? item.user.age,
          accessibility: item.accessibility ?? item.user.accessibility,
          otherAccessibility: item.otherAccessibility ?? item.user.otherAccessibility,
          status: item.status,
        }
      })
    } as IUserBookingTicket;
  }

  const setBookingData = () => {
    setCurrentBookingData({
      minAge: minAge,
      bookingInterest: fillBookingData(bookingInterest),
      isPriority: allocationSession?.allocation?.type === "Priority"
    })
  }

  useEffect(() => {
    if (bookingInterest.id == modalBookingData.bookingInterest.id) {
      setCurrentBookingData({
        minAge: minAge,
        bookingInterest: fillBookingData(bookingInterest),
        isPriority: allocationSession?.allocation?.type === "Priority"
      })
    }

    if (session) {
      const sessionDetailsObj = generateSessionDetails(session);
      setSessionDetails(sessionDetailsObj);

      const showMinimumAge = session?.show?.defaultMinimumAgeRequirement;
      setMinAge(sessionDetailsObj?.minimumAgeRequirement ?? (showMinimumAge === null ? 0 : showMinimumAge))
    }
  }, [bookingInterest]);

  let ticketHolders = [
    {
      user: {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
      },
      assignedFirstName: bookingInterest?.assignedFirstName ?? user.firstName,
      assignedLastName: bookingInterest?.assignedLastName ?? user.lastName,
      ticket: bookingInterest.ticket,
      isLeadBooker: bookingInterest.parentId === null,
      accessibility: bookingInterest.accessibility,
      otherAccessibility: bookingInterest.otherAccessibility,
      hasAccessibilityRequirements: bookingInterest.hasAccessibilityRequirements,
      accessibilityRequestStatus: (bookingInterest.accessibilityRequestStatus === "Accepted" && bookingInterest.hasAccessibilityRequirements) ? "Accepted" : "Pending",
      bookingInterestStatus: status,
      isChildBooking: false,
    },
  ] as TicketDetails[];

  bookingInterest.relatedBookingInterests && bookingInterest.relatedBookingInterests.length <= 5 && bookingInterest.relatedBookingInterests.map(
    (relatedBookingInterest: UserBookingInterest) => {
      ticketHolders.push({
        id: relatedBookingInterest.id!,
        user: {
          ...relatedBookingInterest.user,
          email: relatedBookingInterest.user.email || user.email
        },
        assignedFirstName: relatedBookingInterest.assignedFirstName ?? relatedBookingInterest.user.firstName,
        assignedLastName: relatedBookingInterest.assignedLastName ?? relatedBookingInterest.user.lastName,
        ticket: relatedBookingInterest.ticket,
        isLeadBooker: false,
        isChildBooking: true,
        userAgeAtBooking: relatedBookingInterest.userAgeAtBooking ?? relatedBookingInterest.user.age,
        accessibility: relatedBookingInterest.accessibility,
        otherAccessibility: relatedBookingInterest.otherAccessibility,
        hasAccessibilityRequirements: relatedBookingInterest.hasAccessibilityRequirements,
        accessibilityRequestStatus: relatedBookingInterest.accessibilityRequestStatus,
        bookingInterestStatus: relatedBookingInterest.status,
      });
    }
  );

  bookingInterest.parentAndSiblingInterests && bookingInterest.parentAndSiblingInterests.map(
    (parentAndSiblingInterest: UserBookingInterest) => {
      ticketHolders.push({
        id: parentAndSiblingInterest.id!,
        user: {
          ...parentAndSiblingInterest.user,
          email: parentAndSiblingInterest.user.email || user.email
        },
        assignedFirstName: parentAndSiblingInterest.assignedFirstName ?? parentAndSiblingInterest.user.firstName,
        assignedLastName: parentAndSiblingInterest.assignedLastName ?? parentAndSiblingInterest.user.lastName,
        isLeadBooker: parentAndSiblingInterest.parentId == null,
        isChildBooking: true, //parentAndSiblingInterest.parentId != null,
        userAgeAtBooking: parentAndSiblingInterest.userAgeAtBooking ?? parentAndSiblingInterest.user.age,
        accessibility: parentAndSiblingInterest.accessibility,
        otherAccessibility: parentAndSiblingInterest.otherAccessibility,
        hasAccessibilityRequirements: parentAndSiblingInterest.hasAccessibilityRequirements,
        accessibilityRequestStatus: parentAndSiblingInterest.accessibilityRequestStatus,
        bookingInterestStatus: parentAndSiblingInterest.status,
        isParentOrSibling: true
      });
    }
  );

  const showName = session?.show?.name;
  const sessionDate = dayjs(session?.arrivalTime!);
  const pastShowDeadline = dayjs(session?.approxFinishTime!).add(3, 'hour');

  let isSessionEditable: boolean = false;
  let statusTagVariant: StatusTagVariants = "applicationPending";

  const currentDate = dayjs();

  const hasDependentBookingInterests = () => {
    const dependentBookingInterests = bookingInterest.relatedBookingInterests?.find(bI => bI?.user?.age && bI?.user?.age < 18)
    return !!dependentBookingInterests
  }

  const hasRelatedButNotRespondedBookingInterests = () => {
    const relatedButNotRespondedBookingInterest = bookingInterest.relatedBookingInterests?.find(bI => bI?.status &&
      bI.status === "InterestRegistered" || bI.status === "InvitedToTicket")
    return !!relatedButNotRespondedBookingInterest
  }

  if (pastShowDeadline.isBefore(currentDate)) {
    statusTagVariant = "pastShow";
  } else {
    switch (status) {
      case "InterestRegistered":
        isSessionEditable = true;
        statusTagVariant = "applicationPending";
        break;

      case "InvitedToTicket":
        isSessionEditable = true;
        statusTagVariant = "ticketOffer";
        break;

      case "TicketAccepted":
        isSessionEditable = hasDependentBookingInterests() || hasRelatedButNotRespondedBookingInterests();
        statusTagVariant = "tickets";
        break;

      case "TicketDeclined":
        isSessionEditable = false;
        statusTagVariant = "ticketOffer";
        break;

      case "TicketTimedOut":
        isSessionEditable = false;
        statusTagVariant = "offerExpired";
        break;

      case "TurnedAway":
        isSessionEditable = false;
        statusTagVariant = "turnedAway";
        break;

      case "EnteredSession":
        isSessionEditable = false;
        statusTagVariant = "pastShow";
        break;

      case "UnattendedSession":
        isSessionEditable = false;
        statusTagVariant = "pastShow";
        break;

      case "AttendanceSMSReminderSent":
        isSessionEditable = false;
        statusTagVariant = "checkInPending";
        break;

      case "AttendanceConfirmedViaSMS":
        isSessionEditable = false;
        statusTagVariant = "checkedIn";
        break;

      case "AttendanceDeclinedViaSMS":
        isSessionEditable = false;
        statusTagVariant = "checkInDeclined";
        break;

      case "AttendanceConfirmationTimedOutViaSMS":
        isSessionEditable = false;
        statusTagVariant = "timedoutSms";
        break;

      case "TicketOrInterestRevoked":
        isSessionEditable = false;
        statusTagVariant = "ticketOrInterestRevoked";
        break;

      default:
        isSessionEditable = true;
        statusTagVariant = "applicationPending";
        break;
    }
  }

  const renderDownloadButton = (ticketHolder: TicketDetails) => {
    const ticketId = ticketHolder.ticket?.id;
    const ticketEmail = ticketHolder.user.email;
    const isTicketReady = Boolean(ticketId);
    const isGenerating = isTicketGenerating || !isTicketReady;

    return (
      <Button
        data-cy="download-ticket-button"
        leftIcon={isGenerating ? null : <Icon icon="Download" size="sm" />}
        variant="primary"
        size="sm"
        loading={isDownloadLoading || isGenerating}
        disabled={isGenerating}
        onClick={() => {
          if (isGenerating || !ticketId || !ticketEmail) {
            return;
          }
          handleDownloadTicketInternal(ticketEmail, ticketId);
        }}
        full
      >
        {isGenerating ? "Generating" : "Download"}
      </Button>
    );
  };

  const UserBookingStatusSwitch = (ticketHolder: TicketDetails) => {
    switch (ticketHolder.bookingInterestStatus) {
      case "EnteredSession":
        return <StatusTag variant="attended" />;

      case "UnattendedSession":
        return <StatusTag variant="noShow" />;

      case "TurnedAway":
        return <StatusTag variant="turnedAway" />;

      case "TicketDeclined":
        return <StatusTag variant="declined" />;
      case "TicketOrInterestRevoked":
        return <StatusTag variant="ticketOrInterestRevoked" />;
      case "AttendanceDeclinedViaSMS":
        return <StatusTag variant="checkInDeclined" />;
      case "AttendanceConfirmationTimedOutViaSMS":
        return <StatusTag variant="timedoutSms" />;
      case "TicketTimedOut":
        return <StatusTag variant="timedout" />;
      case "TicketAccepted":
        if (statusTagVariant == "pastShow") {
          return <StatusTag variant="noShow" />;
        } else if (ticketHolder.isChildBooking) {
          return <StatusTag variant="ticketAccepted" />;
        } else {
          return (
            <ButtonsContainer>
              <Button
                variant="secondary"
                size="sm"
                loading={isDeclineLoading}
                onClick={() => handleDeclineTicketInternal(bookingInterest)}
                full
              >
                Cancel Ticket
              </Button>
              {renderDownloadButton(ticketHolder)}
            </ButtonsContainer>
          );
        }
      case "AttendanceConfirmedViaSMS":
        if (statusTagVariant == "pastShow") {
          return <StatusTag variant="noShow" />;
        } else if (ticketHolder.isChildBooking) {
          return <StatusTag variant="checkedIn" />;
        } else {
          return (
            <ButtonsContainer>
              <Button
                variant="secondary"
                data-cy="decline-attendance-button"
                size="sm"
                loading={isDeclineAttendanceLoading}
                onClick={() => handleDeclineAttendanceInternal(bookingInterest)}
                full
              >
                Cancel Ticket
              </Button>
              {renderDownloadButton(ticketHolder)}
            </ButtonsContainer>
          );
        }
      case "AttendanceSMSReminderSent":
        if (statusTagVariant == "pastShow") {
          return <StatusTag variant="noShow" />;
        } else if (ticketHolder.isChildBooking) {
          return <StatusTag variant="checkInPending" />;
        } else {
          return (
            <ButtonsContainer>
              <Button
                variant="secondary"
                data-cy="decline-attendance-button"
                size="sm"
                loading={isDeclineAttendanceLoading}
                onClick={() => handleDeclineAttendanceInternal(bookingInterest)}
                full
              >
                Cancel Ticket
              </Button>
              <Button
                sx={{ minWidth: "max-content" }}
                data-cy="confirm-attendance-button"
                variant="primary"
                size="sm"
                loading={isConfirmAttendanceLoading}
                onClick={() => handleConfirmAttendanceInternal(bookingInterest)}
                full
              >
                Confirm Attendance
              </Button>
            </ButtonsContainer>
          );
        }
      case "InvitedToTicket":
        if (statusTagVariant == "pastShow") {
          return <StatusTag variant="declined" />;
        } else if (ticketHolder.isChildBooking) {
          return <StatusTag variant="awaitingResponse" />;
        } else {
          return (
            <ButtonsContainer>
              <Button
                variant="secondary"
                size="sm"
                loading={isDeclineLoading}
                onClick={() => handleDeclineTicketInternal(bookingInterest)}
                full
              >
                Decline
              </Button>
              <Button
                data-cy="accept-ticket-button"
                variant="primary"
                size="sm"
                loading={isAcceptLoading}
                onClick={() => handleAcceptTicketInternal(bookingInterest)}
                full
              >
                Accept
              </Button>
            </ButtonsContainer>
          );
        }
      default:
        return null;
    }
  };

  const ticketType = getTicketType(ticketHolders, allocationSession?.allocation?.type);
  return (
    <BookingCardContainer {...rest} data-cy="bookingCard" data-id={ticket?.barcode}>
      <BookingCardStatusContainer>
        <StatusTag variant={statusTagVariant} />
      </BookingCardStatusContainer>

      <BookingCardContentContainer>
        <SessionDetails
          showName={showName}
          showMinimumAge={minAge}
          sessionDetails={sessionDetails}
          ticketType={ticketType}
          bookingInterest={bookingInterest}
          isPriority={ticketType === "priority" || ticketType === "priorityAccessibility"}
        />
        {ticketHolders &&
          ticketHolders.sort((holderA, holderB) => holderA.id - holderB.id).map((ticketHolder, index) => {
            return (
              <TicketHolderContainer
                key={`ticket-holder-${bookingInterest.id}-${index}`}
              >
                <TicketIcon
                  ticketNumber={index + 1}
                  ticketIconStyle={
                    statusTagVariant === "pastShow" ||
                      statusTagVariant === "tickets"
                      ? "outlined"
                      : (ticketType === "priority" || ticketType === "priorityAccessibility") ? "gold-outlined" : "dotted"
                  }
                />
                <TicketHolder data-cy="bookingCard-ticketHolder">
                  {ticketHolder.assignedFirstName} {ticketHolder.assignedLastName}
                </TicketHolder>
                <BadgeContainer>
                  {ticketHolder.isLeadBooker && (
                    <Badge badgeStyle="leadBooker" />
                  )}
                  {ticketHolder.userAgeAtBooking && ticketHolder.userAgeAtBooking < 18 && (
                    <Badge badgeStyle="underAge" />
                  )}

                </BadgeContainer>
                {ticketHolder.hasAccessibilityRequirements && (
                  <Box>
                    <Badge badgeStyle="accessibilityRequired" size="sm" />
                  </Box>
                )}
                {/*!ticketHolder.isParentOrSibling && */ticketHolder.bookingInterestStatus != "InterestRegistered" && (
                  <UserStatusContainer>
                    {UserBookingStatusSwitch(ticketHolder)}
                  </UserStatusContainer>
                )}
              </TicketHolderContainer>
            );
          })}

        {bookingInterest.relatedBookingInterests && bookingInterest.relatedBookingInterests.length > 5 &&
          <TicketHolderContainer
            key={`ticket-holder-${bookingInterest.id}-${1}`}
          >
            <TicketIcon
              ticketNumber={1}
              ticketIconStyle={
                statusTagVariant === "pastShow" ||
                  statusTagVariant === "tickets"
                  ? "outlined"
                  : ticketType === "priority" ? "gold-outlined" : "dotted"
              }
            />
            <TicketHolder data-cy="bookingCard-ticketHolder">
              Group ticket with {bookingInterest.relatedBookingInterests.length} guests
            </TicketHolder>
            <BadgeContainer>
              <Badge badgeStyle="groupTicket" />
            </BadgeContainer>
            {bookingInterest.hasAccessibilityRequirements && (
              <Box>
                <Badge badgeStyle="accessibilityRequired" size="sm" />
              </Box>
            )}
          </TicketHolderContainer>
        }
        {isSessionEditable && (
          <SessionCardBottomBar>
            <Countdown dueDate={sessionDate} daysToGoWarning={2} isLiveCountDown={false} />
            <ManageBookingMenu
              setBookingData={setBookingData}
              disableEdit={!!bookingInterest.allocationSessionId && allocationSession?.allocation?.type !== "Priority"}
              onModalAction={onModalAction}
            />
          </SessionCardBottomBar>
        )}
      </BookingCardContentContainer>
      <MeshGradient
        colour={colour}
        position="bottom"
        size="low"
        sx={{
          top: ticketHolders && ticketHolders.length > 0 ? 0 : "auto",
          zIndex: 0,
        }}
      />
    </BookingCardContainer>
  );
};

export interface BookingCardProps
  extends React.ComponentProps<typeof BookingCard> {
  as?: React.ElementType;
}

BookingCard.displayName = "BookingCard";
